import { themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import images from "../../../assets/images";
import Modal from "../../../components/modal/Modal";
import { useDisposalMethodsListStore } from "../../../store/masterDataStore";
import { DisposalMethodsListType } from "../../../types/api";

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

const emptyForm = {
  methodName: "",
  description: "",
  regulatoryBody: "",
  safetyPrecautions: "",
};

const DisposalMethods: React.FC = () => {
  const {
    disposalMethodsList,
    loading,
    error,
    fetchAllDisposalMethodsList,
    createDisposalMethodsList,
    updateDisposalMethodsList,
    deleteDisposalMethodsList,
  } = useDisposalMethodsListStore();

  const [rowData, setRowData] = useState<any[]>([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);
  useEffect(() => {
    const fetchData = async () => {
      await fetchAllDisposalMethodsList();
    };
    fetchData();
  }, []);

  useEffect(() => {
    setRowData(disposalMethodsList);
  }, [disposalMethodsList]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      let response;
      if (isEditMode && editId) {
        response = await updateDisposalMethodsList({ ...formData }, editId);
      } else {
        response = await createDisposalMethodsList(formData);
      }
      if (response) {
        setModalOpen(false);
        setFormData(emptyForm);
        setIsEditMode(false);
        setEditId(null);
      }
    } catch (error) {
      console.error("Error creating disposal method:", error);
    }
  };

  const handleEdit = useCallback(
    (data: DisposalMethodsListType) => {
      setIsEditMode(true);
      setFormData({
        methodName: data.methodName || "",
        description: data.description || "",
        regulatoryBody: data.regulatoryBody || "",
        safetyPrecautions: data.safetyPrecautions || "",
      });
      setEditId(data._id || "");
      setModalOpen(true);
    },
    [setIsEditMode, setFormData, setEditId]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      const confirmed = window.confirm(
        "Are you sure you want to delete this disposal method?"
      );
      if (confirmed) {
        await deleteDisposalMethodsList(id);
      }
    },
    [deleteDisposalMethodsList]
  );

  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const columnDefs = useMemo<any>(
    () => [
      {
        field: "methodName",
        headerName: "Method Name",
        sortable: false,
        // minWidth: 20,
        minWidth: 30,
        maxWidth: 150,
        flex: 0,
        headerStyle: {
          backgroundColor: "#e9f5ff",
        },
        filter: false,
        cellStyle: {
          textAlign: "left",
          padding: "10px 0 0 15px",
        },
      },
      {
        field: "description",
        headerName: "Description",
        sortable: false,
        minWidth: 20,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
        cellStyle: {
          padding: "10px 5px 0 10px",
          textAlign: "left",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        tooltipField: "description",
      },
      {
        field: "regulatoryBody",
        headerName: "Regulatory Body",
        sortable: false,
        minWidth: 20,
        flex: 1,
        headerStyle: { backgroundColor: "#e9f5ff" },
        filter: false,
      },
      {
        field: "action",
        headerName: "Action",
        cellStyle: { padding: "10px 15px 0 0 ", textAlign: "left" },
        // minWidth: 20,
        headerStyle: { backgroundColor: "#e9f5ff" },
        flex: 0,
        minWidth: 40,
        maxWidth: 90,
        cellRenderer: (params: any) => (
          <div className="flex gap-x-3 pt-2">
            <button
              onClick={() => handleEdit(params.data)}
              className="p-1 hover:bg-gray-100 rounded"
              title="Edit Disposal Method"
            >
              <img
                className="iconTd w-5 h-5"
                src={images.editIcon}
                alt="edit"
              />
            </button>
            <button
              onClick={() => handleDelete(params.data._id)}
              className="p-1 hover:bg-red-100 rounded"
              title="Delete Disposal Method"
            >
              <Trash2 className="text-red-500" size={23} />
            </button>
          </div>
        ),
        filter: false,
      },
    ],
    []
  );

  const defaultColDef = useMemo<any>(() => {
    return {
      editable: false,
      filter: false,
      tooltipComponentParams: {
        color: "#ececec",
      },
    };
  }, []);

  const theme = useMemo<any>(() => {
    return gridTheme;
  }, []);

  return (
    <>
      <div className="px-2 py-4 mt-14 min-h-screen">
        <div className="flex justify-between items-center mb-4">
          <h2 className="ml-2">Disposal Method</h2>
          <button
            className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white border border-[#0fa9a6] px-4 py-2 rounded-full hover:bg-teal-600"
            onClick={() => {
              setModalOpen(true);
              setIsEditMode(false);
              setFormData(emptyForm);
            }}
          >
            <Plus size={20} /> Add Disposal Method
          </button>
        </div>

        <div className="tableBlock w-full">
          <div className="card">
            {rowData?.length > 0 ? (
              <div className="ag-theme-alpine w-full min-h-[400px] border-0 rounded-lg shadow-lg">
                <AgGridReact
                  rowData={rowData}
                  columnDefs={columnDefs}
                  theme={theme}
                  headerHeight={65}
                  rowHeight={50}
                  // loading={isLoading}
                  defaultColDef={defaultColDef}
                  domLayout="autoHeight"
                  pagination={true}
                  paginationPageSize={5}
                  paginationPageSizeSelector={[5, 10, 20]}
                  paginationAutoPageSize={false}
                />
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center h-64">
                <p className="text-gray-500 text-lg mb-4">No data added yet</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <Modal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        closeButton={false}
        modalClass="bg-white w-full max-w-3xl rounded-xl shadow-lg relative max-h-[80vh] flex flex-col"
      >
        {/* Scrollable content area */}
        <div className="pt-6 px-6 flex-1 overflow-y-auto">
          <h2 className="text-xl font-semibold text-gray-800 border-b pb-2 mb-6">
            {isEditMode
              ? "Update Disposal Method"
              : "Create New Disposal Method"}
          </h2>
          <form
            onSubmit={handleSubmit}
            id="disposalMethodForm"
            className="flex flex-col gap-6 pb-20"
          >
            {/* Method Name */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="methodName"
                className="text-sm font-medium text-gray-700"
              >
                Method Name <span className="text-red-500">*</span>
              </label>
              <input
                id="methodName"
                name="methodName"
                type="text"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                value={formData.methodName}
                onChange={handleChange}
                placeholder="Enter disposal method name"
                required
                aria-required="true"
                aria-label="Method Name"
              />
            </div>

            {/* Description */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="description"
                className="text-sm font-medium text-gray-700"
              >
                Description
              </label>
              <textarea
                id="description"
                name="description"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none resize-y"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter description or notes"
                rows={3}
                aria-label="Description"
              />
            </div>

            {/* Regulatory Body / Guideline */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="regulatoryBody"
                className="text-sm font-medium text-gray-700"
              >
                Regulatory Body / Guideline
              </label>
              <input
                id="regulatoryBody"
                name="regulatoryBody"
                type="text"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                value={formData.regulatoryBody}
                onChange={handleChange}
                placeholder="Enter regulatory body or guideline"
                aria-label="Regulatory Body or Guideline"
              />
            </div>

            {/* Safety Precautions */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="safetyPrecautions"
                className="text-sm font-medium text-gray-700"
              >
                Safety Precautions
              </label>
              <textarea
                id="safetyPrecautions"
                name="safetyPrecautions"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none resize-y"
                value={formData.safetyPrecautions}
                onChange={handleChange}
                placeholder="Enter safety precautions (optional)"
                rows={3}
                aria-label="Safety Precautions"
              />
            </div>

            {/* Bottom spacer */}
            <div className="h-20" />
          </form>
        </div>

        {/* Sticky footer */}
        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg sticky bottom-0">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
            onClick={() => setModalOpen(false)}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="disposalMethodForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
          >
            {isEditMode ? "Update" : "Create"}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default DisposalMethods;
